import { createApi } from "@reduxjs/toolkit/query/react";
import fetchBaseQueryCSRF from "../helper";
import { useAddKoBiddingMutation } from "../liveBidding/api";

const activations = createApi({
    baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL }),
    reducerPath: "api.activations",
    tagTypes: ["activations"],
    endpoints: (builder) => ({

        getKeywords: builder.query<any, { client: any, page: any, page_size: any, workspace_id: any, match_type: any, country: any, sort: any, filters: any, date: any, compareDate: any }>({
            query: ({ client, page, page_size, workspace_id, match_type, country, sort, filters, date, compareDate }) => {
                return {
                    url: "/activations/keywords",
                    method: "POST",
                    body: JSON.stringify(
                        {
                            client,
                            page,
                            page_size,
                            workspace_id,
                            match_type,
                            country,
                            sort,
                            filters,
                            date,
                            compareDate
                        }
                    ),
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        "Cache-Control": "no-cache",
                    },
                };
            },
            providesTags: ["activations"],
        }),

        getCampaigns: builder.query<any, {
            client: string,
            page: number,
            page_size: number,
            workspace_id?: number,
            match_type?: string,
            country?: string,
            compareDate: any,
            filters: any,
            date: any,
            sort?: any[],
        }>({
            query: ({ client, page, page_size, workspace_id, match_type, country, sort, filters, date, compareDate }) => ({
                url: "/activations/campaigns",
                method: "POST",
                body: {
                    client,
                    page,
                    page_size,
                    workspace_id,
                    match_type,
                    country,
                    sort,
                    filters,
                    date,
                    compareDate
                },
            }),
            providesTags: ["activations"],
        }),

        disableActivations: builder.mutation<any, { ids: number[] }>({
            query: ({ ids }) => ({
                url: "/activations/disable",
                method: "POST",
                body: JSON.stringify({ ids }),
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }),
        }),



        // SCRAPING
        addScraping: builder.mutation<any, { keywords: string[], country: string, frequency: string, device: string, mode: string, home_language: string, platform_scraping: string[] }>({
            query: (arg) => ({
                url: '/activations/scraping/add',
                method: 'POST',
                body: arg,
                headers: {
                    'Cache-Control': 'max-age=0',
                    Accept: 'application/json',
                },
            }),
        }),

        disableScraping: builder.mutation<any, { id: number }>({
            query: ({ id }) => ({
                url: '/activations/scraping/disable',
                method: 'PUT',
                body: JSON.stringify({ id }),
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
        }),




        // ACTIVE MODE
        addActiveMode: builder.mutation<any, Partial<any>>({
            query: (newStrategy) => ({
                url: '/activations/active-mode/add',
                method: 'POST',
                body: newStrategy,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
        }),
        disableActiveMode: builder.mutation<any, { id: number; workspace_id: string }>({
            query: ({ id, workspace_id }) => ({
                url: '/activations/active-mode/disable',
                method: 'POST',
                body: JSON.stringify({ id, workspace_id }),
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
        }),



        // KO BIDDING MANUAL
        addKoBiddingManualMaxCpc: builder.mutation<any, Partial<any>>({
            query: (data) => ({
                url: "/activations/ko-bidding-manual/add/max-cpc",
                method: "POST",
                body: data,
                headers: {
                    "Content-Type": "application/json",
                },
            }),
        }),
        addKoBiddingManualCampaign: builder.mutation<any, Partial<any>>({
            query: (data) => ({
                url: "/activations/ko-bidding-manual/add/campaign",
                method: "POST",
                body: data,
                headers: {
                    "Content-Type": "application/json",
                },
            }),
        }),
        disableKoBiddingManualMaxCpc: builder.mutation<any, { id: number }>({
            query: ({ id }) => ({
                url: "/activations/ko-bidding-manual/disable/max-cpc",
                method: "POST",
                body: JSON.stringify({ id }),
                headers: {
                    "Content-Type": "application/json",
                },
            }),
            invalidatesTags: ["activations"],
        }),
        updateKoBiddingManual: builder.mutation<any, Partial<any>>({
            query: (data) => ({
                url: "/activations/ko-bidding-manual/update",
                method: "POST",
                body: data,
                headers: {
                    "Content-Type": "application/json",
                },
            }),
        }),



        // KO BIDDING AUTO
        addKoBiddingAutoMaxCpc: builder.mutation<any, Partial<any>>({
            query: (data) => ({
                url: "/activations/ko-bidding-auto/add/max-cpc",
                method: "POST",
                body: data,
                headers: {
                    "Content-Type": "application/json",
                },
            }),
        }),
        disableKoBiddingAutoMaxCpc: builder.mutation<any, { id: number }>({
            query: ({ id }) => ({
                url: "/activations/ko-bidding-auto/disable/max-cpc",
                method: "POST",
                body: JSON.stringify({ id }),
                headers: {
                    "Content-Type": "application/json",
                },
            }),
        }),
        updateKoBiddingAutoMaxCpc: builder.mutation<any, Partial<any>>({
            query: (data) => ({
                url: "/activations/ko-bidding-auto/update/max-cpc",
                method: "POST",
                body: data,
                headers: {
                    "Content-Type": "application/json",
                },
            }),
        }),

        // CSV IMPORT
        exportGoogleSheet: builder.mutation<
            { url: string } | { status: "oauth_required"; auth_url: string },
            {
                workspace_id: number;
                client: string;
                match_type: "EXACT" | "OTHER";
                country: string;
                filters: any;
            }
        >({
            query: (body) => ({
                url: "/activations/csv-import/export-google-sheet",
                method: "POST",
                body,
                headers: { "Content-Type": "application/json" },
            }),
        }),

        exportCsvTemplate: builder.mutation<Blob, {
            workspace_id: number;
            client: string;
            match_type: "EXACT" | "OTHER";
            country: string;
            filters: any;
        }>({
            query: (body) => ({
                url: "/activations/csv-import/export-template",
                method: "POST",
                body,
                headers: { "Content-Type": "application/json" },
                responseHandler: async (response) => {
                    if (!response.ok) return response.json();
                    return response.blob();
                },
            }),
        }),
        importCsv: builder.mutation<any, {
            file: File;
            workspace_id: number;
            client: string;
            match_type: "EXACT" | "OTHER";
            country: string;
            filters: any;
            dry_run: boolean;
        }>({
            query: ({ file, workspace_id, client, match_type, country, filters, dry_run }) => {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("workspace_id", String(workspace_id));
                formData.append("client", client);
                formData.append("match_type", match_type);
                formData.append("country", country);
                formData.append("filters", JSON.stringify(filters));
                formData.append("dry_run", dry_run ? "true" : "false");
                return {
                    url: "/activations/csv-import/import",
                    method: "POST",
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, arg) => arg.dry_run ? [] : ["activations"],
        }),


        getScatterPlot: builder.query<
            any,
            {
                client_id: string;
                keyword: string;
                platform: string;
                region_name: string;
                match_type: string;
                ad_group_id?: number;
            }
        >({
            query: ({ client_id, keyword, platform, region_name, match_type, ad_group_id }) => ({
                url: "/activations/scatter-plot",
                method: "POST",
                body: {
                    client_id,
                    keyword,
                    platform,
                    region_name,
                    match_type,
                    ad_group_id,
                },
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "Cache-Control": "no-cache",
                },
            }),
            providesTags: ["activations"],
        }),

    }),
});

export const {
    useGetKeywordsQuery,
    useDisableActivationsMutation,

    useGetCampaignsQuery,

    // SCRAPING
    useAddScrapingMutation,
    useDisableScrapingMutation,

    // ACTIVE MODE
    useAddActiveModeMutation,
    useDisableActiveModeMutation,

    // KO BIDDING MANUAL
    useAddKoBiddingManualMaxCpcMutation,
    useDisableKoBiddingManualMaxCpcMutation,
    useUpdateKoBiddingManualMutation,
    useAddKoBiddingManualCampaignMutation,

    // KO BIDDING AUTO
    useAddKoBiddingAutoMaxCpcMutation,
    useDisableKoBiddingAutoMaxCpcMutation,
    useUpdateKoBiddingAutoMaxCpcMutation,

    // CSV IMPORT
    useExportGoogleSheetMutation,
    useExportCsvTemplateMutation,
    useImportCsvMutation,


    // SCATTER PLOT
    useGetScatterPlotQuery
} = activations;

export default activations;
